import { zodResolver } from '@hookform/resolvers/zod';
import type { BillingNote } from '@policyvision/contracts';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { BillingNoteTemplatePicker } from '@/billing/component/BillingNoteTemplatePicker';
import { useUpdateBillingNote } from '@/billing/hooks/useUpdateBillingNote';
import { billingNoteInputSchema } from '@/billing/types/BillingNoteInput';
import { Button } from '@/common/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from '@/common/components/ui/form';

export type UpdateBillingNoteFormProps = {
  billingUuid: string;
  note: BillingNote;
  onDone: () => void;
};

const resolver = zodResolver(z.object({ note: billingNoteInputSchema }));

const UpdateBillingNoteForm = ({
  billingUuid,
  note,
  onDone,
}: UpdateBillingNoteFormProps) => {
  const { t } = useTranslation();
  const { updateNote, isPending } = useUpdateBillingNote();

  const form = useForm({
    resolver,
    mode: 'onChange',
    defaultValues: {
      note:
        note.templateId !== null
          ? { templateId: note.templateId }
          : { content: note.content ?? '' },
    },
  });

  const { isDirty, isValid } = form.formState;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((values) =>
          updateNote(
            { billingUuid, noteUuid: note.uuid, note: values.note },
            {
              onSuccess: () => {
                onDone();
                toast.success(t('billing.notesHistory.noteUpdated'));
              },
              onError: () =>
                toast.error(t('billing.notesHistory.noteUpdateError')),
            },
          ),
        )}
        className="space-y-2"
      >
        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <BillingNoteTemplatePicker {...field} />
              </FormControl>
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onDone}
            disabled={isPending}
          >
            {t('actions.cancel')}
          </Button>
          <Button type="submit" disabled={!isDirty || !isValid || isPending}>
            {t('actions.save')}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default UpdateBillingNoteForm;
